/**
 * Grid3x3 (AM4) — 3×3 grid result screen with header, re-brew chip,
 * cross-sell card, and sticky 4-button action bar.
 *
 * Design source: services/astro-bot-miniapp/design/calestea/project/flow-avatar-mini.jsx
 *   function AM4Grid() (lines 427–556).
 */

import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import WebApp from '@twa-dev/sdk';
import type { AvatarFrame } from '../../../types/avatarFlow';
import styles from './Grid3x3.module.css';

export interface Grid3x3Props {
  frames: AvatarFrame[];
  onSelectFrame: (index: number) => void;
  /** Tap "← Pack" / "← Reveal" — back to previous step. Defaults to no-op. */
  onBack?: () => void;
  /** Tap re-brew chip — triggers paid re-roll. Defaults to no-op. */
  onRebrew?: () => void;
  /** Tap cross-sell card — open the cross-sell SKU detail/invoice. */
  onCrossSell?: () => void;
  /** Localized pack name (e.g. "Whimsigoth pack"). */
  packName?: string;
  /** Override the kicker copy (defaults to "{frameCount} OF {frameCount} READY"). */
  kicker?: string;
  testId?: string;
}

async function downloadFrame(url: string, fileName: string): Promise<void> {
  const res = await fetch(url, { mode: 'cors' });
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function tgPopup(message: string): void {
  try {
    if (typeof WebApp.showPopup === 'function') {
      WebApp.showPopup({ message });
      return;
    }
  } catch {
    /* fall through */
  }
  try {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') window.alert(message);
  } catch {
    /* noop */
  }
}

export function Grid3x3({
  frames,
  onSelectFrame,
  onBack,
  onRebrew,
  onCrossSell,
  packName,
  kicker,
  testId = 'result-grid-3x3',
}: Grid3x3Props): JSX.Element {
  const { t } = useTranslation();
  const visibleFrames = frames.slice(0, 9);
  const readyKicker =
    kicker ??
    t('avatarFlow.grid.kicker', {
      done: visibleFrames.length,
      total: visibleFrames.length,
      defaultValue: `Mini pack · ${visibleFrames.length} of ${visibleFrames.length} ready`,
    });
  const titlePrefix = t('avatarFlow.grid.titlePrefix', { defaultValue: 'Your' });
  const titleAccent = packName ?? t('avatarFlow.grid.titleAccent', { defaultValue: 'Whimsigoth pack' });
  const hint = t('avatarFlow.grid.hint', {
    defaultValue: 'Tap any frame to view full size · long-press to save',
  });
  const rebrewCopy = t('avatarFlow.grid.rebrewCopy', {
    defaultValue: "Don't love a frame? 1 free re-brew left in 24h.",
  });
  const rebrewCta = t('avatarFlow.grid.rebrewCta', { defaultValue: 'RE-BREW ›' });
  const crossTitle = t('avatarFlow.grid.crossTitle', {
    defaultValue: 'Want 7 more in different aesthetics?',
  });
  const crossSub = t('avatarFlow.grid.crossSub', {
    defaultValue: 'Max pack · 16 frames · 3 cultural lenses',
  });
  // Cross-sell navigates to /sku/avatar_max → the FULL Max-pack price (not a delta).
  const crossPrice = t('avatarFlow.grid.crossPrice', {
    stars: SKU_CATALOG['avatar_max'].stars,
    defaultValue: `${SKU_CATALOG['avatar_max'].stars} ⭐`,
  });
  async function handleSaveAll(): Promise<void> {
    if (visibleFrames.length === 0) return;
    let saved = 0;
    for (const frame of visibleFrames) {
      if (!frame.imageUrl) continue;
      try {
        await downloadFrame(frame.imageUrl, `${frame.id}.png`);
        saved += 1;
      } catch {
        /* keep going */
      }
    }
    tgPopup(
      saved > 0
        ? t('avatarFlow.grid.savedToast', {
            count: saved,
            defaultValue: 'Saved {{count}} portraits to your device.',
          })
        : t('avatarFlow.grid.saveFailed', {
            defaultValue: 'Could not save the portraits. Long-press each image to save manually.',
          }),
    );
  }

  async function handleShareAll(): Promise<void> {
    const first = visibleFrames[0];
    if (!first?.imageUrl) return;
    const shareText = t('avatarFlow.grid.shareText', {
      defaultValue: 'My Calestea stellar portraits ✦',
    });
    try {
      const fn = (WebApp as unknown as { shareToStory?: (url: string, opts?: { text?: string }) => void })
        .shareToStory;
      if (typeof fn === 'function') {
        fn(first.imageUrl, { text: shareText });
        return;
      }
    } catch {
      /* fall through */
    }
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: first.imageUrl, text: shareText });
        return;
      } catch {
        return;
      }
    }
    tgPopup(
      t('avatarFlow.grid.shareUnavailable', {
        defaultValue: 'Sharing is not available on this device.',
      }),
    );
  }

  async function handleSetTg(): Promise<void> {
    const first = visibleFrames[0];
    if (first?.imageUrl) {
      try {
        await downloadFrame(first.imageUrl, `${first.id}.png`);
      } catch {
        /* best-effort */
      }
    }
    tgPopup(
      t('avatarFlow.grid.setTgHint', {
        defaultValue:
          'Saved. Open Telegram → Settings → tap your avatar → Set Photo, then choose this image.',
      }),
    );
  }

  function handlePrivacy(): void {
    tgPopup(
      t('avatarFlow.grid.privacyInfo', {
        defaultValue:
          'Your pack is private. Calestea never publishes your images and your selfie is auto-deleted after generation.',
      }),
    );
  }

  const actions: ReadonlyArray<{ key: string; ico: string; label: string; onClick: () => void }> = [
    {
      key: 'save',
      ico: '⤓',
      label: t('avatarFlow.grid.actionSaveAll', { defaultValue: 'Save all' }),
      onClick: () => void handleSaveAll(),
    },
    {
      key: 'set-tg',
      ico: '☷',
      label: t('avatarFlow.grid.actionSetTg', { defaultValue: 'Set TG' }),
      onClick: () => void handleSetTg(),
    },
    {
      key: 'share',
      ico: '↗',
      label: t('avatarFlow.grid.actionShare', { defaultValue: 'Share' }),
      onClick: () => void handleShareAll(),
    },
    {
      key: 'privacy',
      ico: '🔒',
      label: t('avatarFlow.grid.actionPrivacy', { defaultValue: 'Privacy' }),
      onClick: handlePrivacy,
    },
  ];

  return (
    <div className={styles.root} data-testid={testId}>
      <div className={styles.scroll}>
        <div className={styles.topbar}>
          <button type="button" className={styles.topbarBack} onClick={onBack}>
            ← {t('avatarFlow.grid.backReveal', { defaultValue: 'Reveal' })}
          </button>
          <span className={styles.topbarMore} aria-hidden="true">· · ·</span>
        </div>

        <div className={styles.header}>
          <div className={styles.kicker}>{readyKicker}</div>
          <h1 className={styles.title}>
            {titlePrefix} <em className={styles.titleAccent}>{titleAccent}</em>
          </h1>
          <div className={styles.hint}>{hint}</div>
        </div>

        <div className={styles.grid} role="list">
          {visibleFrames.map((frame, index) => (
            <button
              key={frame.id}
              type="button"
              role="listitem"
              className={styles.cell}
              onClick={() => onSelectFrame(index)}
              data-testid={`result-grid-cell-${index}`}
              aria-label={t('avatarFlow.grid.frameLabel', {
                n: index + 1,
                defaultValue: `Frame ${index + 1}`,
              })}
            >
              {frame.thumbnailUrl ? <img src={frame.thumbnailUrl} alt="" className={styles.thumb} /> : null}
            </button>
          ))}
        </div>

        <div
          className={styles.rebrew}
          role={onRebrew ? 'button' : undefined}
          tabIndex={onRebrew ? 0 : -1}
          onClick={onRebrew}
          onKeyDown={(e) => {
            if (onRebrew && (e.key === 'Enter' || e.key === ' ')) onRebrew();
          }}
        >
          <span className={styles.rebrewIco} aria-hidden="true">↻</span>
          <div className={styles.rebrewCopy} dangerouslySetInnerHTML={{ __html: rebrewCopy }} />
          <button type="button" className={styles.rebrewCta} onClick={onRebrew}>{rebrewCta}</button>
        </div>

        <div
          className={styles.crosssell}
          role={onCrossSell ? 'button' : undefined}
          tabIndex={onCrossSell ? 0 : -1}
          onClick={onCrossSell}
          onKeyDown={(e) => {
            if (onCrossSell && (e.key === 'Enter' || e.key === ' ')) onCrossSell();
          }}
        >
          <div className={styles.crosssellGlyph} aria-hidden="true">✧</div>
          <div>
            <div className={styles.crosssellTitle}>{crossTitle}</div>
            <div className={styles.crosssellSub}>{crossSub}</div>
          </div>
          <div className={styles.crosssellPrice}>{crossPrice}</div>
        </div>

        <div className={styles.bottomSpacer} />
      </div>

      <div className={styles.actionbar}>
        <div className={styles.actionrow}>
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              className={styles.actionbtn}
              data-testid={`grid-action-${a.key}`}
              onClick={a.onClick}
            >
              <span className={styles.actionbtnIco} aria-hidden="true">{a.ico}</span>
              <span className={styles.actionbtnLabel}>{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
